'use client';

import * as Sentry from '@sentry/nextjs';
import { useState } from 'react';
import { type Control, useForm } from 'react-hook-form';
import type { FieldValues, RegisterOptions } from 'react-hook-form';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import FormCheckbox from '../../../../components/form/FormCheckbox/FormCheckbox';
import FormDate from '../../../../components/form/FormDate/FormDate';
import FormSelect from '../../../../components/form/FormSelect/FormSelect';
import FormText from '../../../../components/form/FormText/FormText';
import FormTextArea from '../../../../components/form/FormTextArea/FormTextArea';
import { ModalBase } from '../../../../components/ui';
import { swal } from '../../../../lib';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import layoutStyles from '../../../../styles/modules/layout.module.scss';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { isValidDateString } from '../../../../utils/date';
import { WCHANCE_TYPE } from '../../constants';
import type { WChanceSurveyFormItem } from '../../types';
import styles from './SurveyForm.module.scss';
import currentStyles from './SurveyForm.module.scss';

export interface SurveyFormProps {
  id: string;
  hideSubmit?: boolean;
  isPreview?: boolean;
  customForms: WChanceSurveyFormItem[];
  lotteryEventId: number;
  lotteryDrawHistoryId?: number;
  lotteryPrizeLabelId?: number;
  wChanceType?: 'manual' | 'direct' | 'batch';
  isModalOpen: boolean;
  handleModalClose: () => void;
  handleNextClick: () => void;
  // 外部依存関数
  onSubmit: (data: FieldValues) => Promise<void>;
}

function SurveyForm({
  hideSubmit = false,
  isPreview = false,
  customForms,
  lotteryDrawHistoryId,
  lotteryPrizeLabelId,
  wChanceType,
  isModalOpen,
  handleModalClose,
  handleNextClick,
  onSubmit,
}: SurveyFormProps) {
  const [isLoading, setIsLoading] = useState(false);

  // デフォルト値を配列にしてをまとめて返す関数
  const createDefaultValues = (forms: WChanceSurveyFormItem[] = []): FieldValues => {
    const values = forms.reduce((acc, form) => {
      switch (form.type) {
        case 'checkbox':
          acc[form.hash] = form.value ? (Array.isArray(form.value) ? form.value : [form.value]) : [];
          break;
        case 'radio':
          acc[form.hash] = form.value ? String(form.value) : '';
          break;
        default:
          acc[form.hash] = form.value != null ? String(form.value) : '';
      }
      return acc;
    }, {} as FieldValues);

    return values;
  };

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: createDefaultValues(customForms),
  });

  const submit = async (data: FieldValues) => {
    if (isLoading) return;
    if (!lotteryDrawHistoryId && !lotteryPrizeLabelId) return;
    setIsLoading(true);
    try {
      await onSubmit(data);
    } catch (error) {
      // エラーをSentryに送信する
      Sentry.captureException(error);
      if (error instanceof Error) {
        await showError([error.message]);
      } else {
        await showError(['必須項目が入力されていません。ご確認の上、再度入力をお願いいたします。']);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // エラー表示関数
  const showError = async (messages: string[]) => {
    await swal.messages({ messages });
  };

  const submitError = () => {
    swal.invalid();
  };

  // フォームタイプに応じたバリデーションルールを生成
  const getRules = (formItem: WChanceSurveyFormItem): RegisterOptions | undefined => {
    switch (formItem.type) {
      // チェックボックスの場合
      case 'checkbox':
        return {
          validate: (value: string[]) => {
            // 必須ではない場合はtrueを返してバリデーションをスキップ
            if (!formItem.required) return true;
            // value配列が空である場合は必須エラーとする
            if (value.length === 0) return '入力に誤りがあります（必須項目です）';
            return true;
          },
        };
      // ラジオボタンの場合
      case 'radio':
        return {
          validate: (value: string | number | null | undefined) => {
            // 必須ではない場合はtrueを返してバリデーションをスキップ
            if (!formItem.required) return true;
            // valueが空の場合は必須エラーとする
            if (value === '' || value == null || value === undefined) return '入力に誤りがあります（必須項目です）';
            return true;
          }
        };
      // テキストの場合
      case 'text':
        return {
          validate: (value: string | null | undefined) => {
            // 必須ではない場合はtrueを返してバリデーションをスキップ
            if (!formItem.required) return true;
            // valueが空の場合は必須エラーとする
            if (value === '' || value == null || value === undefined) return '入力に誤りがあります（必須項目です）';
            // valueが0の場合はエラーとする
            if (value === '0') return '入力に誤りがあります（入力不可：「0」）';
            return true;
          },
        };
      // テキストエリアの場合
      case 'textarea':
        return {
          validate: (value: string | null | undefined) => {
            // 必須ではない場合はtrueを返してバリデーションをスキップ
            if (!formItem.required) return true;
            // valueが空の場合は必須エラーとする
            if (value === '' || value == null || value === undefined) return '入力に誤りがあります（必須項目です）';
            // valueが0の場合はエラーとする
            if (value === '0') return '入力に誤りがあります（入力不可：「0」）';
            return true;
          },
        };
      // 日付の場合: 年・月・日がすべて入力されており、有効な日付であること(valueは"YYYY-MM-DD"の形式で渡される)
      case 'date':
        return {
          validate: (value: string | null | undefined) => {
            if (formItem.required) {
              // 日付が必須の場合

              // valueが空の場合は必須エラーとする
              if (value === '' || value == null || value === undefined) return '入力に誤りがあります（必須項目です）';
              // valueが有効な日付文字列でない場合はエラーとする
              if (!isValidDateString(value || '', 'yyyy-M-d')) return '入力に誤りがあります（日付ではありません）';
              return true;
            }
            // 日付が必須ではない場合

            // valueが空の場合はチェックを通す
            if (value === '' || value == null || value === undefined) return true;
            // valueが有効な日付文字列でない場合はエラーとする
            if (!isValidDateString(value || '', 'yyyy-M-d')) return '入力に誤りがあります（日付ではありません）';
            return true;
          },
        };
      // それ以外(url/number/email/tel)の場合
      default:
        return {
          validate: (value: string | null | undefined) => {
            // 必須ではない場合はtrueを返してバリデーションをスキップ
            if (!formItem.required) return true;
            // valueが空の場合は必須エラーとする
            if (value === '' || value == null || value === undefined) return '入力に誤りがあります（必須項目です）';
            return true;
          },
        };
    }
  };

  const customFormItemJsx = (
    customFormItem: WChanceSurveyFormItem,
    control: Control<FieldValues>,
    index: number,
    styles: Record<string, string>,
  ) => {
    const rules = getRules(customFormItem);

    const labelWithRequired = (
      <p>
        <span className={styles.label}>
          <span className={styles.questionNumber}>質問{index + 1}.　</span>
          {customFormItem.label}
        </span>
        {customFormItem.required && <span className={styles.required}>*</span>}
      </p>
    );

    switch (customFormItem.type) {
      case 'text':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              rules={rules}
              placeholder={customFormItem.placeholder || undefined}
              outlined
            />
          </div>
        );
      case 'number':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              rules={rules}
              placeholder={customFormItem.placeholder || undefined}
              type='number'
            />
          </div>
        );
      case 'email':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              rules={rules}
              placeholder={customFormItem.placeholder || undefined}
              outlined
              type='email'
            />
          </div>
        );
      case 'tel':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              rules={rules}
              placeholder={customFormItem.placeholder || undefined}
              outlined
              type='tel'
            />
          </div>
        );
      case 'textarea':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormTextArea
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              rules={rules}
              placeholder={customFormItem.placeholder || undefined}
              outlined
            />
          </div>
        );
      case 'url':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              rules={rules}
              placeholder={customFormItem.placeholder || undefined}
              outlined
            />
          </div>
        );
      case 'checkbox':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormCheckbox
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              rules={rules}
              // 必須マークは常に非表示にするためtrueを渡す
              isForceHideRequiredMark={true}
              items={
                customFormItem.options?.map((option) => ({
                  value: option.value.toString(),
                  child: <p className={styles.optionLabel}>{option.label}</p>,
                })) || []
              }
            />
          </div>
        );
      case 'radio':
        // biome-ignore lint/correctness/noSwitchDeclarations: <explanation>
        const options =
          customFormItem.options?.map((item) => ({
            value: item.value.toString(),
            text: item.label,
          })) || [];
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormSelect
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              rules={rules}
              items={[{ value: '', text: '選択してください' }].concat(options)}
            />
          </div>
        );
    case 'date':
      return (
        <div className={styles.fullWidth} key={customFormItem.hash}>
          {labelWithRequired}
          <FormDate
            required={customFormItem.required}
            name={customFormItem.hash}
            control={control}
            placeholder={customFormItem.placeholder || undefined}
            rules={rules}
          />
        </div>
      );
      default:
        return <div key={customFormItem.hash} />;
    }
  };

  // 送信ボタンのdisabled状態を管理
  const isSubmitDisabled = () => {
    // ローディング中は常にボタンが押せない状態にする
    if (isLoading) return true;
    // バリデーションが通っていない場合はボタンを押せない状態にする
    if (!isValid) return true;
    // それ以外は送信ボタンを押下できる状態にする
    return false;
  };

  return (
    <>
      {/* フォームタイトル */}
      <div className={`${currentStyles.mtSmall}`}>
        <p className={`${currentStyles.textSmall} ${currentStyles.textBold}`}>{isPreview && 'アンケートプレビュー'}</p>
      </div>

      {/* 入力フォーム */}
      <div className={`${currentStyles.mtSmall} ${layoutStyles.formWrapper}`}>
        {customForms.map((form, index) => (
          <div className={currentStyles.fullWidth} key={form.hash}>
            <span className={currentStyles.required}>{customFormItemJsx(form, control, index, currentStyles)}</span>
          </div>
        ))}
      </div>

      {/* submitボタン （プレビューモードでは非表示） */}
      {!isPreview && !hideSubmit && (
        <div className={`${currentStyles.centeredVertical} ${currentStyles.mtMedium}`}>
          <ActionButton disabled={isSubmitDisabled()} onClick={handleSubmit(submit, submitError)} isLoading={isLoading}>
            入力を完了する
          </ActionButton>
        </div>
      )}
      {/* エントリー完了モーダル */}
      {wChanceType === WCHANCE_TYPE.batch && (
        <ModalBase isOpen={isModalOpen} onClose={handleModalClose}>
          <div className={` ${utilsStyles.padding} ${styles.modalContainer}`}>
            <div className={`${utilsStyles.textCenter} ${utilsStyles.mbMedium}`}>
              <div className={`${styles.wchanceHeader} ${styles.wchanceHeaderText} ${utilsStyles.mtSmall}`}>
                <span className={fontsStyles.montserrat}>W</span>チャンス賞
              </div>
              <p className={`${utilsStyles.textLarge} ${utilsStyles.textBold} ${utilsStyles.mtSmall}`}>
                エントリーが完了しました
              </p>
            </div>
            <div className={`${utilsStyles.centered} ${utilsStyles.mtSmall} `}>
              <ActionButton size='large' color='primary' onClick={handleNextClick}>
                次へ進む
              </ActionButton>
            </div>
          </div>
        </ModalBase>
      )}
    </>
  );
}

export default SurveyForm;
